import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import NavLink from '../common/NavLink';
import { getIdToken } from '../Firebase';
import { slugify } from '../../utils/slugify';

const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_MAKE_API_KEY,
};

const hasGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const leagueLabel = (row) => (
  hasGroup(row.group)
    ? `${row.name} (${row.group.trim()})`
    : row.name
);

const LeaguesWithoutUpcomingMatches = () => {
  const [days, setDays] = useState(15);
  const [appliedDays, setAppliedDays] = useState(15);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [missingResolution, setMissingResolution] = useState(null);
  const [selectedTeamsBySource, setSelectedTeamsBySource] = useState({});
  const [renameLoadingName, setRenameLoadingName] = useState(null);
  const [createOptions, setCreateOptions] = useState(null);
  const [createFormsBySource, setCreateFormsBySource] = useState({});
  const [createLoadingName, setCreateLoadingName] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async (daysToLoad) => {
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken(false);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/leagues-without-upcoming-matches`,
        {
          params: { days: daysToLoad },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setRows(Array.isArray(response.data?.leagues) ? response.data.leagues : []);
      setAppliedDays(response.data?.days || daysToLoad);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(15);
  }, [load]);

  const loadTeams = useCallback(async () => {
    if (teams.length > 0) return teams;
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/teams`);
    const nextTeams = Array.isArray(response.data) ? response.data : [];
    setTeams(nextTeams);
    return nextTeams;
  }, [teams]);

  const loadCreateOptions = useCallback(async (competitionId) => {
    const token = await getIdToken(false);
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/admin/team-create`,
      {
        params: { competitionId },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    setCreateOptions(response.data || {});
    return response.data || {};
  }, []);

  const initialCreateForms = (missingTeams, options) => {
    const defaults = options?.competitionDefaults || {};
    return missingTeams.reduce((acc, sourceName) => ({
      ...acc,
      [sourceName]: {
        name: sourceName,
        countryId: defaults.countryId || 40,
        regionId: defaults.regionId || '',
        districtId: defaults.districtId || '',
        eloRanking: options?.suggestedElo ?? '',
      },
    }), {});
  };

  const reloadWithDays = () => {
    const normalizedDays = Math.max(1, Math.min(Number(days) || 15, 365));
    setDays(normalizedDays);
    load(normalizedDays);
  };

  const fetchCompetitionData = async (row) => {
    setActionLoadingId(`/api/updateLeague-${row.competitionId}`);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/updateLeague`,
        { competitionId: row.competitionId },
        { headers: API_HEADERS }
      );
      setMissingResolution(null);
      setMessage(response.data?.message || `Pobrano dane dla ${leagueLabel(row)}`);
      await load(appliedDays);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.status === 'missing_teams') {
        const missingTeams = Array.isArray(err.response.data?.missingTeams) ? err.response.data.missingTeams : [];
        setMissingResolution({ row, missingTeams });
        setSelectedTeamsBySource({});
        setMessage('');
        setError(err.response.data?.message || 'W źródle znaleziono drużyny bez dopasowania w bazie.');
        try {
          const options = await loadCreateOptions(row.competitionId);
          setCreateFormsBySource(initialCreateForms(missingTeams, options));
          await loadTeams();
        } catch (teamsErr) {
          setError(`${err.response.data?.message || 'Brakujące drużyny.'} Nie udało się pobrać listy drużyn: ${teamsErr.message}`);
        }
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const renameTeam = async (sourceName) => {
    const selectedTeam = selectedTeamsBySource[sourceName];
    if (!selectedTeam || !missingResolution?.row) return;

    setRenameLoadingName(sourceName);
    setError('');
    setMessage('');
    try {
      const token = await getIdToken(false);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/team-rename`,
        {
          sourceName,
          teamId: selectedTeam.teamId,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setMessage(response.data?.message || `Zmieniono nazwę ${selectedTeam.teamName} na ${sourceName}`);
      setTeams((current) => current.map((team) => (
        team.teamId === selectedTeam.teamId ? { ...team, teamName: sourceName } : team
      )));
      setMissingResolution((current) => {
        if (!current) return current;
        const remaining = current.missingTeams.filter((name) => name !== sourceName);
        return remaining.length > 0 ? { ...current, missingTeams: remaining } : null;
      });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setRenameLoadingName(null);
    }
  };

  const updateCreateForm = (sourceName, field, value) => {
    setCreateFormsBySource((current) => ({
      ...current,
      [sourceName]: {
        ...(current[sourceName] || { name: sourceName }),
        [field]: value,
      },
    }));
  };

  const applySuggestedElo = (sourceName) => {
    if (createOptions?.suggestedElo === null || createOptions?.suggestedElo === undefined) return;
    updateCreateForm(sourceName, 'eloRanking', createOptions.suggestedElo);
  };

  const createTeam = async (sourceName) => {
    const form = createFormsBySource[sourceName] || {};
    if (!missingResolution?.row) return;

    setCreateLoadingName(sourceName);
    setError('');
    setMessage('');
    try {
      const token = await getIdToken(false);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/team-create`,
        {
          name: form.name || sourceName,
          competitionId: missingResolution.row.competitionId,
          countryId: Number(form.countryId),
          regionId: Number(form.regionId),
          districtId: Number(form.districtId),
          eloRanking: Number(form.eloRanking),
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setMessage(response.data?.message || `Dodano druĹĽynÄ™ ${form.name || sourceName}`);
      setTeams((current) => [
        ...current,
        {
          teamId: response.data?.teamId,
          teamName: response.data?.name || form.name || sourceName,
          eloRanking: response.data?.eloRanking,
        },
      ]);
      setMissingResolution((current) => {
        if (!current) return current;
        const remaining = current.missingTeams.filter((name) => name !== sourceName);
        return remaining.length > 0 ? { ...current, missingTeams: remaining } : null;
      });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setCreateLoadingName(null);
    }
  };

  const canCreateTeam = (sourceName) => {
    const form = createFormsBySource[sourceName] || {};
    return Boolean(
      form.name
      && form.countryId !== ''
      && form.countryId !== null
      && form.countryId !== undefined
      && form.regionId !== ''
      && form.regionId !== null
      && form.regionId !== undefined
      && form.districtId !== ''
      && form.districtId !== null
      && form.districtId !== undefined
      && form.eloRanking !== ''
      && !Number.isNaN(Number(form.eloRanking))
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Ładowanie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Ligi bez najbliższych meczów
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ligi aktywnego sezonu bez zaplanowanego meczu z konkretnym terminem w najbliższym oknie dni.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            type="number"
            size="small"
            label="Dni"
            value={days}
            onChange={(event) => setDays(event.target.value)}
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 110 }}
          />
          <Button variant="outlined" onClick={reloadWithDays} disabled={loading || Boolean(actionLoadingId)}>
            Przeładuj
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {missingResolution && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#2b2418', border: '1px solid rgba(255,202,40,0.35)' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Brakujące drużyny w źródle
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {leagueLabel(missingResolution.row)}: wybierz istniejącą drużynę, której nazwa ma zostać zmieniona na nazwę z 90minut, a potem ponów pobieranie danych.
          </Typography>
          <Stack spacing={1.5}>
            {missingResolution.missingTeams.map((sourceName) => (
              <Stack key={sourceName} spacing={1} alignItems="stretch" sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1 }}>
                <Box sx={{ minWidth: { md: 260 } }}>
                  <Typography sx={{ fontWeight: 700 }}>{sourceName}</Typography>
                  <Typography variant="caption" color="text.secondary">nazwa z 90minut</Typography>
                </Box>
                <Autocomplete
                  size="small"
                  options={teams}
                  value={selectedTeamsBySource[sourceName] || null}
                  onOpen={loadTeams}
                  onChange={(_, value) => setSelectedTeamsBySource((current) => ({ ...current, [sourceName]: value }))}
                  getOptionLabel={(option) => option?.teamName ? `${option.teamName} (#${option.teamId})` : ''}
                  isOptionEqualToValue={(option, value) => option.teamId === value.teamId}
                  renderInput={(params) => <TextField {...params} label="Drużyna do zmiany nazwy" />}
                  sx={{ flex: 1, minWidth: { md: 360 } }}
                />
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => renameTeam(sourceName)}
                  disabled={!selectedTeamsBySource[sourceName] || Boolean(renameLoadingName)}
                >
                  {renameLoadingName === sourceName ? <CircularProgress size={16} /> : 'Zmień nazwę'}
                </Button>
                <Divider flexItem>albo dodaj nową</Divider>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }}>
                  <TextField
                    size="small"
                    label="Nazwa"
                    value={(createFormsBySource[sourceName] || { name: sourceName }).name || ''}
                    onChange={(event) => updateCreateForm(sourceName, 'name', event.target.value)}
                    sx={{ minWidth: { lg: 250 } }}
                  />
                  <FormControl size="small" sx={{ minWidth: { lg: 180 } }}>
                    <InputLabel>Kraj</InputLabel>
                    <Select
                      label="Kraj"
                      value={(createFormsBySource[sourceName] || {}).countryId || ''}
                      onChange={(event) => updateCreateForm(sourceName, 'countryId', event.target.value)}
                    >
                      {(createOptions?.countries || []).map((country) => (
                        <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: { lg: 180 } }}>
                    <InputLabel>Region</InputLabel>
                    <Select
                      label="Region"
                      value={(createFormsBySource[sourceName] || {}).regionId || ''}
                      onChange={(event) => updateCreateForm(sourceName, 'regionId', event.target.value)}
                    >
                      {(createOptions?.regions || []).map((region) => (
                        <MenuItem key={region.id} value={region.id}>{region.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: { lg: 180 } }}>
                    <InputLabel>District</InputLabel>
                    <Select
                      label="District"
                      value={(createFormsBySource[sourceName] || {}).districtId || ''}
                      onChange={(event) => updateCreateForm(sourceName, 'districtId', event.target.value)}
                    >
                      {(createOptions?.districts || []).map((district) => (
                        <MenuItem key={district.id} value={district.id}>{district.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    label="Elo"
                    value={(createFormsBySource[sourceName] || {}).eloRanking ?? ''}
                    onChange={(event) => updateCreateForm(sourceName, 'eloRanking', event.target.value)}
                    sx={{ width: { lg: 110 } }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => applySuggestedElo(sourceName)}
                    disabled={createOptions?.suggestedElo === null || createOptions?.suggestedElo === undefined}
                  >
                    Średnia ligi - 100
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => createTeam(sourceName)}
                    disabled={!canCreateTeam(sourceName) || Boolean(createLoadingName)}
                  >
                    {createLoadingName === sourceName ? <CircularProgress size={16} /> : 'Dodaj drużynę'}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={`Okno: ${appliedDays} dni`} />
        <Chip size="small" label={`Liczba lig: ${rows.length}`} />
      </Stack>

      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e' }}>
        <Table size="small" sx={{ minWidth: 880 }}>
          <TableHead>
            <TableRow>
              <TableCell>Liga</TableCell>
              <TableCell>Sezon</TableCell>
              <TableCell>Źródło</TableCell>
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak lig bez najbliższych meczów.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const competitionUrl = `/liga/${row.competitionId}/${slugify(leagueLabel(row))}`;
              const fetchLoading = actionLoadingId === `/api/updateLeague-${row.competitionId}`;

              return (
                <TableRow hover key={row.competitionId}>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography
                        component={NavLink}
                        to={competitionUrl}
                        sx={{ color: '#90caf9', fontWeight: 700 }}
                      >
                        {leagueLabel(row)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        competition_id: {row.competitionId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.season || '-'} />
                  </TableCell>
                  <TableCell>
                    {row.link ? (
                      <MuiLink href={row.link} target="_blank" rel="noreferrer">
                        90minut
                      </MuiLink>
                    ) : (
                      <Typography variant="body2" color="text.secondary">brak</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => fetchCompetitionData(row)}
                      disabled={Boolean(actionLoadingId)}
                    >
                      {fetchLoading ? <CircularProgress size={16} /> : 'Aktualizuj'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default LeaguesWithoutUpcomingMatches;
